/**
 * The month grid. FR-301, FR-303, FR-308.
 *
 * Encoding rule, from UI design Section 3: **never colour alone.** Every state
 * carries a texture, glyph or outline as well, and every cell has a screen
 * reader label that states the same information in words. A calendar that only
 * works for a sighted user with typical colour vision is not a calendar.
 */
import { en, t } from '@copy/index'
import {
  addDays,
  dayOfWeek,
  daysInMonth,
  startOfMonth,
  type DayEntry,
  type FlowLevel,
  type ISODate,
} from '@domain/index'

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

/**
 * Fill and texture per flow level.
 *
 * The texture is what carries the meaning when colour is unavailable —
 * high-contrast mode, colour-blindness, a greyscale screenshot.
 */
const FLOW_STYLE: Record<FlowLevel, { className: string; glyph: string }> = {
  none: { className: '', glyph: '' },
  spotting: { className: 'bg-transparent border border-flow-spotting', glyph: '·' },
  light: { className: 'bg-flow-light/35', glyph: '░' },
  medium: { className: 'bg-flow-medium/65 text-white', glyph: '▓' },
  heavy: { className: 'bg-flow-heavy text-white', glyph: '█' },
}

export interface MonthGridProps {
  /** Any date within the month to render. */
  month: ISODate
  entries: ReadonlyMap<ISODate, DayEntry>
  today: ISODate
  /** Estimated next period start, when one exists. Rendered as provisional. */
  predictedStart?: ISODate | undefined
  selected?: ISODate | undefined
  onSelectDay: (date: ISODate) => void
}

export function MonthGrid({
  month,
  entries,
  today,
  predictedStart,
  selected,
  onSelectDay,
}: MonthGridProps) {
  const first = startOfMonth(month)
  const [year, month1] = first.split('-').map(Number) as [number, number]
  const total = daysInMonth(year, month1)

  // Monday-first. Leading blanks align the 1st under its weekday.
  const leadingBlanks = dayOfWeek(first)
  const days: ISODate[] = Array.from({ length: total }, (_, i) => addDays(first, i))

  return (
    <div>
      <div className="grid grid-cols-7 gap-1 pb-2" aria-hidden="true">
        {WEEKDAY_KEYS.map((key) => (
          <div key={key} className="text-center text-xs font-medium text-ink-muted">
            {t(`calendar.weekday.${key}`)}
          </div>
        ))}
      </div>

      <div role="grid" aria-label={monthLabel(first)} className="grid grid-cols-7 gap-1">
        {Array.from({ length: leadingBlanks }, (_, i) => (
          <div key={`blank-${i}`} role="presentation" />
        ))}

        {days.map((date) => (
          <DayCell
            key={date}
            date={date}
            entry={entries.get(date)}
            isToday={date === today}
            isPredicted={date === predictedStart}
            isSelected={date === selected}
            onSelect={onSelectDay}
          />
        ))}
      </div>
    </div>
  )
}

interface DayCellProps {
  date: ISODate
  entry: DayEntry | undefined
  isToday: boolean
  isPredicted: boolean
  isSelected: boolean
  onSelect: (date: ISODate) => void
}

function DayCell({ date, entry, isToday, isPredicted, isSelected, onSelect }: DayCellProps) {
  const dayNumber = Number(date.slice(8, 10))
  const flow = entry?.flow
  const hasFlowFill = flow && flow !== 'none'
  const style = hasFlowFill ? FLOW_STYLE[flow] : undefined

  // FR-004: a logged day with nothing recorded is visually distinct from a day
  // that was never touched. A faint outline, not an absence.
  const loggedEmpty = entry !== undefined && !hasFlowFill
  const hasSymptoms = (entry?.symptoms?.length ?? 0) > 0

  return (
    <button
      type="button"
      role="gridcell"
      onClick={() => onSelect(date)}
      aria-label={cellLabel(date, entry, { isToday, isPredicted })}
      aria-current={isToday ? 'date' : undefined}
      aria-selected={isSelected}
      className={[
        // 44px minimum touch target. NFR-10.
        'relative flex min-h-11 min-w-11 flex-col items-center justify-center rounded-lg',
        'text-sm transition-colors',
        'hover:bg-line/60',
        style?.className ?? (loggedEmpty ? 'border border-dashed border-line' : ''),
        // FR-304: an estimate must never render like recorded data. Dashed
        // outline, no fill — provisional at a glance, and distinguishable from
        // the dashed-but-solid-bordered "logged nothing" state above.
        isPredicted && !flow ? 'border-2 border-dashed border-accent' : '',
        isToday ? 'ring-2 ring-accent ring-offset-1 ring-offset-surface' : '',
        isSelected ? 'outline outline-2 outline-accent' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <span>{dayNumber}</span>
      {style ? (
        // Redundant with the fill, and that is the point: this survives
        // greyscale, high contrast, and every form of colour blindness.
        <span aria-hidden="true" className="text-[0.6rem] leading-none opacity-80">
          {style.glyph}
        </span>
      ) : null}

      {/* Note and symptom markers use different shapes as well as different
          corners, so they remain distinguishable without colour. */}
      {entry?.note ? (
        <span
          aria-hidden="true"
          className="absolute right-1 top-1 h-1 w-1 rounded-full bg-current opacity-60"
        />
      ) : null}
      {hasSymptoms ? (
        <span
          aria-hidden="true"
          className="absolute bottom-1 left-1 h-1 w-1.5 bg-current opacity-60"
        />
      ) : null}
    </button>
  )
}

/**
 * The spoken label. NFR-10.
 *
 * Every visual signal above has to be conveyed here in words, because colour,
 * fill, glyph and dashed border are all invisible to a screen reader. This is
 * the reason the visual encoding and the label are built in the same function:
 * adding a marker without a word for it is then obvious.
 */
function cellLabel(
  date: ISODate,
  entry: DayEntry | undefined,
  flags: { isToday: boolean; isPredicted: boolean },
): string {
  const readable = t.date(new Date(`${date}T12:00:00`))
  const parts: string[] = [readable]

  if (flags.isToday) parts.push(t('calendar.today'))

  if (entry?.flow) parts.push(t('calendar.labelFlow', { flow: flowName(entry.flow) }))
  else if (entry !== undefined) parts.push(t('calendar.labelLoggedEmpty'))

  const symptomCount = entry?.symptoms?.length ?? 0
  if (symptomCount > 0) {
    parts.push(t.plural(symptomCountForms, symptomCount))
  }

  if (entry?.note) parts.push(t('calendar.labelNote'))
  if (entry?.pain !== undefined) parts.push(t('entry.painValue', { value: entry.pain }))

  // Said last and said as an estimate, so it is never mistaken for a record.
  if (flags.isPredicted) parts.push(t('calendar.labelPredicted'))

  return parts.join(', ')
}

const flowName = (flow: FlowLevel) => t(`entry.flowLevel.${flow}`)

/**
 * Plural forms live in the copy layer like every other string (ADR-0005).
 * `t.plural` routes through `Intl.PluralRules`, so a locale with six plural
 * categories rather than two needs no code change here.
 */
const symptomCountForms = {
  one: en.calendar.labelSymptomCount.one,
  other: en.calendar.labelSymptomCount.other,
}

function monthLabel(monthStart: ISODate): string {
  return t.date(new Date(`${monthStart}T12:00:00`), { month: 'long', year: 'numeric' })
}

export { monthLabel }
